import React, { useState, useEffect } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { LabelTemplate } from '@/types/label';
import { Check, AlertCircle, Code } from 'lucide-react';

interface JsonEditorProps {
  template: LabelTemplate;
  onUpdateTemplate: (json: string) => boolean;
}

export const JsonEditor: React.FC<JsonEditorProps> = ({
  template,
  onUpdateTemplate,
}) => {
  const [json, setJson] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setJson(JSON.stringify(template, null, 2));
    setError(null);
    setSaved(false);
  }, [template]);

  const handleApply = () => {
    try {
      JSON.parse(json); // Validate JSON
      const success = onUpdateTemplate(json);
      if (success) {
        setError(null);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } else {
        setError('Invalid template structure');
      }
    } catch (e) {
      setError('Invalid JSON syntax');
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-3 border-b border-border bg-card">
        <div className="flex items-center gap-2">
          <Code className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Template JSON</span>
        </div>
        <Button
          size="sm"
          onClick={handleApply}
          className="gap-2"
        >
          {saved ? <Check className="w-4 h-4" /> : null}
          {saved ? 'Applied' : 'Apply Changes'}
        </Button>
      </div>
      
      <div className="flex-1 p-4">
        <Textarea
          value={json}
          onChange={(e) => {
            setJson(e.target.value);
            setError(null);
            setSaved(false);
          }}
          className="h-full min-h-[400px] font-mono text-sm resize-none"
          placeholder="Template JSON..."
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 mx-4 mb-4 text-sm text-destructive bg-destructive/10 rounded-md">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}
    </div>
  );
};
